import {ChangeDetectorRef, Component, Input, OnChanges, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';

import {forkJoin, merge, Observable, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, finalize, map, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';

import isEqual from 'lodash-es/isEqual';

import {Object} from 'core-js';
import {Destroyable} from '../../../destroyable.component';
import {RolesService} from '../../authentication/roles/roles.service';
import {DefaultPagination, Pagination} from '../../ui';
import {SortDirection} from '../../ui/table/table.constants';
import {SortKey} from '../../ui/table/table.interfaces';
import {WindowService} from '../../ui/window.service';
import {User, UserService} from '../../user';
import {Enums} from '../../utils/enums';
import {PersonPayrollLogStatuses, SdcEventTypes, SdcLogDTO, SdcLogStatuses} from '../sdconnect.model';
import {SdconnectService} from '../sdconnect.service';

@Component({
  selector: 'gp-sdc-logs-list',
  templateUrl: './sdc-logs-list.component.html',
  styleUrls: ['./sdc-logs-list.component.scss']
})
export class SdcLogsListComponent extends Destroyable implements OnInit, OnChanges {

  @Input()
  public contractId: string = '';

  @Input()
  public includedTypes: string[] = [];

  @Input()
  public excludedTypes: string[] = [];

  public timezone: string;

  public sdcLogs$: Observable<SdcLogDTO[]>;
  public personLogStatuses$: Observable<{ status: string; count: string | number }[]>;

  public statuses: string[] = Enums.getNames(SdcLogStatuses);

  public eventTypes: string[] = Enums.getNames(SdcEventTypes);

  public personPayrollLogStatuses: string[] = Enums.getNames(PersonPayrollLogStatuses);

  public pagination: Pagination = new DefaultPagination(15);

  public sortKey: SortKey = [{key: 'updateDate', direction: SortDirection.DESCENDING}];

  public sdcLogsLoading = false;
  public personLogStatusesLoading = false;

  public selectedLog: SdcLogDTO;

  public filtersForm: UntypedFormGroup = this.formBuilder.group({
    creationDate: [''],
    updateDate: [''],
    externalReference: [''],
    contractId: [''],
    payGroup: [''],
    eventType: [''],
    status: [''],
    correlationId: ['']
  });

  private hasSDC3AdminRole: boolean;

  private hasSDC2AdminRole: boolean;

  private reload$ = new Subject<boolean>();

  constructor(private userService: UserService,
              private sdconnectService: SdconnectService,
              private formBuilder: UntypedFormBuilder,
              private rolesService: RolesService,
              private windowService: WindowService,
              private cd: ChangeDetectorRef) {
    super();
  }

  public ngOnInit() {
    this.userService.getUser()
      .pipe(
        tap((user: User) => this.timezone = user.timezone),
        takeUntil(this.componentDestroyed$)
      ).subscribe();

    const filterChanges$ = this.filtersForm.valueChanges
      .pipe(
        startWith(this.filtersForm.value as object),
        distinctUntilChanged(isEqual)
      );

    const pageChanges$ = this.pagination.pageChanged.asObservable();

    this.sdcLogs$ = merge(filterChanges$, pageChanges$, this.reload$)
      .pipe(
        debounceTime(300),
        switchMap(() => this.getSdcLogs()),
        takeUntil(this.componentDestroyed$)
      );

    this.personLogStatuses$ = merge(filterChanges$, this.reload$)
      .pipe(
        debounceTime(300),
        switchMap(() => this.getPersonLogsTotalCounts()),
        takeUntil(this.componentDestroyed$)
      );

    this.hasSDC3AdminRole = this.rolesService.hasRole('SDC3_ADMIN');

    this.hasSDC2AdminRole = this.rolesService.hasRole('SDCONNECT_ADMIN');

    this.setEventTypeList();
  }

  public ngOnChanges() {
    this.reload();
  }

  public reload() {
    this.reload$.next(true);
    this.pagination.first();
  }

  public onSortKeyChange(key: SortKey): void {
    this.sortKey = key;
    this.reload();
  }

  public openSidePanel(sdcLog: SdcLogDTO) {
    // avoid opening side panel if text selected in cell
    if (this.windowService.isSelectionActive()) {
      return;
    }
    this.selectedLog = sdcLog;
  }

  public closeSidePanel() {
    this.selectedLog = null;
  }

  public resetFilters(): void {
    this.filtersForm.reset({
      creationDate: '',
      updateDate: '',
      externalReference: '',
      contractId: '',
      payGroup: '',
      eventType: '',
      status: '',
      correlationId: ''
    });
  }

  public noFilterSet() {
    return Object.values(this.filtersForm.value).every(value => {
      return value === '';
    });
  }

  private getPersonLogsTotalCounts(): Observable<{ status: string, count: string | number }[]> {
    this.personLogStatusesLoading = true;
    this.cd.markForCheck();

    return forkJoin(
      this.personPayrollLogStatuses.map(status => this.getPersonLogsTotalCount(status)
        .pipe(
          map(count => ({status, count}))
        )
      )
    ).pipe(
      finalize(() => {
        this.personLogStatusesLoading = false;
        this.cd.markForCheck();
      })
    );
  }

  private getPersonLogsTotalCount(status: string): Observable<string | number> {
    return this.sdconnectService.getPersonPayrollLogsBySdcLogSearch('person-logs',
      `latest==true;status==${status}`,
      this.contractId || this.filtersForm.get('contractId')?.value,
      this.filtersForm.get('creationDate')?.value,
      this.filtersForm.get('updateDate')?.value,
      this.filtersForm.get('externalReference')?.value,
      this.filtersForm.get('payGroup')?.value,
      this.filtersForm.get('eventType')?.value,
      this.filtersForm.get('status')?.value,
      this.filtersForm.get('correlationId')?.value,
      this.includedTypes,
      this.excludedTypes, 0, 0)
      .pipe(
        map(value => value.total)
      );
  }

  private getSdcLogs(): Observable<SdcLogDTO[]> {
    this.sdcLogsLoading = true;
    this.cd.markForCheck();

    const orderBy = this.sortKey.map(element => {
      const prefix = element.direction === SortDirection.DESCENDING ? '-' : '';
      return `${prefix}${element.key}`;
    }).join(',');

    return this.sdconnectService.getSdcLogsWithParams(
      this.contractId || this.filtersForm.get('contractId')?.value,
      this.filtersForm.get('creationDate')?.value,
      this.filtersForm.get('updateDate')?.value,
      this.filtersForm.get('externalReference')?.value,
      this.filtersForm.get('payGroup')?.value,
      this.filtersForm.get('eventType')?.value,
      this.filtersForm.get('status')?.value,
      this.filtersForm.get('correlationId')?.value,
      this.includedTypes,
      this.excludedTypes,
      orderBy,
      this.pagination.perPage,
      this.pagination.currentPage.number)
      .pipe(
        tap((result: { total: number, data: SdcLogDTO[] }) => {
          this.pagination.update(result.total);
        }),
        map((result: { total: number, data: SdcLogDTO[] }) => {
          return result.data;
        }),
        finalize(() => {
          this.sdcLogsLoading = false;
          this.cd.markForCheck();
        })
      );
  }

  private setEventTypeList() {
    if (this.hasSDC3AdminRole && this.hasSDC2AdminRole) {
      return;
    }

    this.eventTypes = this.eventTypes.filter(
      eventType => this.hasSDC2AdminRole !== (eventType === SdcEventTypes.SDC3)
    );

    this.eventTypes = this.eventTypes.filter(eventType => this.includedTypes?.includes(eventType));
    this.eventTypes = this.eventTypes.filter(eventType => !this.excludedTypes?.includes(eventType));
  }
}
